import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
    Alert,
    Button,
    Col,
    Divider,
    Empty,
    InputNumber,
    message,
    Popconfirm,
    Row,
    Select,
    Space,
    Spin,
    Tag,
    Typography
} from 'antd';
import { debounce } from 'lodash';
import PropTypes from 'prop-types';

import { ClearOutlined, SaveOutlined } from '@ant-design/icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { TRADEBOOK } from '../../../utilities/functions';

import EvaluationChecklist from './EvaluationChecklist';

import './styles.scss';

const { Text, Title } = Typography;
const { Option } = Select;

const TRADE_CATEGORIES = [
    { value: 'Cat 1', label: 'Cat 1 disciplined trade Won', color: '#52c41a' },
    { value: 'Cat 2', label: 'Cat 2 Discipline Trade Lost', color: '#73d13d' },
    { value: 'Cat 3', label: 'Cat 3 Non-Disciplined Trade Lost', color: '#faad14' },
    { value: 'Cat 4', label: 'Cat 4 Non-Disciplined Trade Won', color: '#ff4d4f' }
];

// Configuration matching StandardFields.js - defines all criterion sections
const CRITERION_SECTIONS = [
    {
        section: 'Setup & Timing',
        fields: [
            { key: 'indicators', checkedKey: 'checkedIndicators', label: 'Indicators' },
            { key: 'confirmations', checkedKey: 'checkedConfirmations', label: 'Confirmations' },
            { key: 'entrySignal', checkedKey: 'checkedEntrySignal', label: 'Entry Signal' },
            { key: 'stopLoss', checkedKey: 'checkedStopLoss', label: 'Stop Loss' },
            { key: 'targets', checkedKey: 'checkedTargets', label: 'Targets' },
            { key: 'time', checkedKey: 'checkedTime', label: 'Time' }
        ]
    },
    {
        section: 'Rules & Notes',
        fields: [
            { key: 'specificRules', checkedKey: 'checkedSpecificRules', label: 'Specific Rules' }
        ]
    }
];

// Helper to get initial empty checked criteria state
const getEmptyCheckedCriteria = () => ({
    checkedTime: [],
    checkedIndicators: [],
    checkedConfirmations: [],
    checkedEntrySignal: [],
    checkedStopLoss: [],
    checkedTargets: [],
    checkedRiskPerTrade: [],
    checkedProfitTargetPerTrade: [],
    checkedMaxLossPerTrade: [],
    checkedStopLossMechanism: [],
    checkedPartialPlan: [],
    checkedAddingToPosition: [],
    checkedHowToManageStop: [],
    checkedSpecificRules: []
});

// Helper to get initial checked criteria from evaluation
const getInitialCheckedCriteria = (evaluation) => ({
    checkedTime: evaluation?.checkedTime || [],
    checkedIndicators: evaluation?.checkedIndicators || [],
    checkedConfirmations: evaluation?.checkedConfirmations || [],
    checkedEntrySignal: evaluation?.checkedEntrySignal || [],
    checkedStopLoss: evaluation?.checkedStopLoss || [],
    checkedTargets: evaluation?.checkedTargets || [],
    checkedRiskPerTrade: evaluation?.checkedRiskPerTrade || [],
    checkedProfitTargetPerTrade: evaluation?.checkedProfitTargetPerTrade || [],
    checkedMaxLossPerTrade: evaluation?.checkedMaxLossPerTrade || [],
    checkedStopLossMechanism: evaluation?.checkedStopLossMechanism || [],
    checkedPartialPlan: evaluation?.checkedPartialPlan || [],
    checkedAddingToPosition: evaluation?.checkedAddingToPosition || [],
    checkedHowToManageStop: evaluation?.checkedHowToManageStop || [],
    checkedSpecificRules: evaluation?.checkedSpecificRules || []
});

export default function TradeEvaluation({ tradeId, initialEvaluation, disabled }) {
    const queryClient = useQueryClient();
    const [selectedPlanId, setSelectedPlanId] = useState(
        initialEvaluation?.tradingPlanRef?._id || initialEvaluation?.tradingPlanRef || null
    );

    // State for all criterion fields
    const [checkedCriteria, setCheckedCriteria] = useState(() =>
        getInitialCheckedCriteria(initialEvaluation)
    );

    const [strategySet, setStrategySet] = useState(initialEvaluation?.strategySet ?? null);
    const [noStrategySet, setNoStrategySet] = useState(initialEvaluation?.noStrategySet ?? null);
    const [tradeCategory, setTradeCategory] = useState(initialEvaluation?.tradeCategory || null);
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
    const [setValidationError, setSetValidationError] = useState(null);
    const previousPlanIdRef = useRef(null);

    // Fetch all trading plans for dropdown
    const { isLoading: plansLoading, data: plans } = useQuery(
        ['tradebook-plans'],
        TRADEBOOK.fetchTradingPlans,
        { retry: false }
    );

    // Fetch criteria for selected plan
    const { isLoading: criteriaLoading, data: criteria } = useQuery(
        ['tradebook-criteria', selectedPlanId],
        () => TRADEBOOK.fetchPlanCriteria(selectedPlanId),
        {
            enabled: !!selectedPlanId,
            retry: false
        }
    );

    // Save mutation
    const saveMutation = useMutation(
        (evaluationData) => TRADEBOOK.saveTradeEvaluation(tradeId, evaluationData),
        {
            onSuccess: () => {
                message.success('Evaluation saved');
                setHasUnsavedChanges(false);
                setSetValidationError(null);
                queryClient.invalidateQueries(['tradebook-stats']);
                // Invalidate strategy sets for the selected plan
                if (selectedPlanId) {
                    queryClient.invalidateQueries(['strategy-sets', selectedPlanId]);
                }
            },
            onError: (error) => {
                // Handle set limit exceeded error
                if (error?.response?.data?.error === 'SET_LIMIT_EXCEEDED') {
                    const { field, message: errorMessage } = error.response.data;
                    setSetValidationError({ field, message: errorMessage });
                    message.warning(errorMessage);
                } else {
                    message.error('Failed to save evaluation');
                    console.error('Save error:', error);
                }
            }
        }
    );

    // Clear mutation
    const clearMutation = useMutation(() => TRADEBOOK.clearTradeEvaluation(tradeId), {
        onSuccess: () => {
            const previousPlanId = selectedPlanId;
            message.success('Evaluation cleared');
            setSelectedPlanId(null);
            setCheckedCriteria(getEmptyCheckedCriteria());
            setStrategySet(null);
            setNoStrategySet(null);
            setTradeCategory(null);
            setHasUnsavedChanges(false);
            setSetValidationError(null);
            queryClient.invalidateQueries(['tradebook-stats']);
            // Invalidate strategy sets for the previously selected plan
            if (previousPlanId) {
                queryClient.invalidateQueries(['strategy-sets', previousPlanId]);
            }
        },
        onError: (error) => {
            message.error('Failed to clear evaluation');
            console.error('Clear error:', error);
        }
    });

    // Initialize checklist when criteria loads or plan changes
    useEffect(() => {
        if (criteria && selectedPlanId !== previousPlanIdRef.current) {
            const newCheckedCriteria = {};

            // Initialize all criterion fields from criteria
            CRITERION_SECTIONS.forEach((section) => {
                section.fields.forEach((field) => {
                    const criteriaItems = criteria.criteria?.[field.key] || [];
                    const existingChecked = checkedCriteria[field.checkedKey] || [];

                    newCheckedCriteria[field.checkedKey] = criteriaItems.map((item) => {
                        // Check if we have existing evaluation for this criterion
                        // Support both old format (indicatorId/confirmationId) and new format (criterionId)
                        const existingItem = existingChecked.find(
                            (ci) =>
                                ci.criterionId?.toString() === item._id?.toString() ||
                                ci.indicatorId?.toString() === item._id?.toString() ||
                                ci.confirmationId?.toString() === item._id?.toString()
                        );
                        return {
                            criterionId: item._id,
                            text: item.text,
                            checked: existingItem ? existingItem.checked : false
                        };
                    });
                });
            });

            // Only reset if plan actually changed (not on initial load with existing evaluation)
            if (previousPlanIdRef.current !== null) {
                setCheckedCriteria(newCheckedCriteria);
                setHasUnsavedChanges(true);
            } else if (!initialEvaluation?.tradingPlanRef) {
                // First load with no existing evaluation
                setCheckedCriteria(newCheckedCriteria);
            }

            previousPlanIdRef.current = selectedPlanId;
        }
    }, [criteria, selectedPlanId]);

    // Debounced auto-save
    const debouncedSave = useRef(
        debounce((data) => {
            if (data.tradingPlanRef) {
                saveMutation.mutate(data);
            }
        }, 300)
    ).current;

    // Build evaluation data
    const buildEvaluationData = useCallback(
        () => ({
            tradingPlanRef: selectedPlanId,
            ...checkedCriteria,
            strategySet,
            noStrategySet,
            tradeCategory
        }),
        [selectedPlanId, checkedCriteria, strategySet, noStrategySet, tradeCategory]
    );

    // Handle plan selection change
    const handlePlanChange = (planId) => {
        setSelectedPlanId(planId);
        setHasUnsavedChanges(true);
    };

    // Handle any criteria field change
    const handleCriteriaChange = (checkedKey, updatedItems) => {
        setCheckedCriteria((prev) => ({
            ...prev,
            [checkedKey]: updatedItems
        }));
        setHasUnsavedChanges(true);

        const data = {
            ...buildEvaluationData(),
            [checkedKey]: updatedItems
        };
        debouncedSave(data);
    };

    // Handle numeric field changes with validation
    const handleNumericChange = async (field, value) => {
        const setValue = {
            strategySet: setStrategySet,
            noStrategySet: setNoStrategySet
        }[field];

        // Clear any previous validation error
        setSetValidationError(null);

        // If value is being set and we have a trading plan selected, validate
        if (value !== null && value !== undefined && selectedPlanId) {
            try {
                const validationData = {
                    tradeId,
                    planId: selectedPlanId,
                    strategySet: field === 'strategySet' ? value : strategySet,
                    noStrategySet: field === 'noStrategySet' ? value : noStrategySet
                };

                const result = await TRADEBOOK.validateSetAssignment(validationData);

                if (!result.valid) {
                    const error = result.errors.find(
                        (e) =>
                            (field === 'strategySet' && e.setType === 'strategy') ||
                            (field === 'noStrategySet' && e.setType === 'noStrategy')
                    );

                    if (error) {
                        setSetValidationError({
                            field,
                            message: error.message
                        });
                        message.warning(error.message);
                        return; // Don't update the value
                    }
                }
            } catch (error) {
                console.error('Validation error:', error);
                // Continue with update even if validation fails (backend will catch it)
            }
        }

        setValue(value);
        setHasUnsavedChanges(true);

        const data = {
            ...buildEvaluationData(),
            [field]: value
        };
        debouncedSave(data);
    };

    // Handle trade category change
    const handleTradeCategoryChange = (value) => {
        const categoryValue = value ? value.value : null;
        setTradeCategory(categoryValue);
        setHasUnsavedChanges(true);

        const data = {
            ...buildEvaluationData(),
            tradeCategory: categoryValue
        };
        debouncedSave(data);
    };

    // Manual save
    const handleManualSave = () => {
        if (!selectedPlanId) {
            message.warning('Please select a TradeBook first');
            return;
        }

        const data = buildEvaluationData();
        saveMutation.mutate(data);
    };

    // Clear evaluation
    const handleClear = () => {
        clearMutation.mutate();
    };

    if (plansLoading) {
        return (
            <div className="trade-evaluation-loading">
                <Spin size="small" />
                <Text>Loading trading plans...</Text>
            </div>
        );
    }

    if (!plans || plans.length === 0) {
        return (
            <div className="trade-evaluation-empty">
                <Empty
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                    description="No trading plans available"
                >
                    <Text type="secondary">
                        Create a trading plan in the TradeBook tab to evaluate trades.
                    </Text>
                </Empty>
            </div>
        );
    }

    return (
        <div className="trade-evaluation">
            {/* TradeBook Selection */}
            <div className="evaluation-section">
                <Title level={5}>Select TradeBook</Title>
                <Select
                    placeholder="Select a TradeBook to evaluate against"
                    value={selectedPlanId}
                    onChange={handlePlanChange}
                    disabled={disabled}
                    style={{ width: '100%' }}
                    allowClear
                    showSearch
                    optionFilterProp="children"
                >
                    {plans.map((plan) => (
                        <Option key={plan._id} value={plan._id}>
                            {plan.strategyName}
                        </Option>
                    ))}
                </Select>
            </div>

            {selectedPlanId && (
                <>
                    <Divider />

                    {criteriaLoading ? (
                        <div className="trade-evaluation-loading">
                            <Spin size="small" />
                            <Text>Loading criteria...</Text>
                        </div>
                    ) : (
                        <>
                            {/* Criteria Checklists - Organized by Section */}
                            {CRITERION_SECTIONS.map((section) => {
                                // Filter to only show fields that have criteria
                                const fieldsWithCriteria = section.fields.filter(
                                    (field) => criteria?.criteria?.[field.key]?.length > 0
                                );

                                // Skip empty sections
                                if (fieldsWithCriteria.length === 0) return null;

                                return (
                                    <div key={section.section} className="evaluation-section-group">
                                        <Title
                                            level={5}
                                            style={{ marginBottom: 16, color: '#666' }}
                                        >
                                            {section.section}
                                        </Title>
                                        <Row gutter={[24, 24]}>
                                            {fieldsWithCriteria.map((field) => (
                                                <Col xs={24} md={12} key={field.key}>
                                                    <EvaluationChecklist
                                                        title={field.label}
                                                        fieldKey={field.key}
                                                        items={
                                                            criteria?.criteria?.[field.key] || []
                                                        }
                                                        checkedItems={
                                                            checkedCriteria[field.checkedKey]
                                                        }
                                                        onChange={(updatedItems) =>
                                                            handleCriteriaChange(
                                                                field.checkedKey,
                                                                updatedItems
                                                            )
                                                        }
                                                        disabled={disabled}
                                                    />
                                                </Col>
                                            ))}
                                        </Row>
                                        <Divider />
                                    </div>
                                );
                            })}

                            {/* Trade Category Selection */}
                            <div className="evaluation-section">
                                <Title level={5}>Trade Category</Title>
                                <Text
                                    type="secondary"
                                    style={{ display: 'block', marginBottom: 12 }}
                                >
                                    Categorize this trade for Discipline % calculation. Cat 1 & Cat
                                    2 are considered disciplined trades.
                                </Text>
                                <Select
                                    placeholder="Select a trade category"
                                    value={
                                        tradeCategory
                                            ? {
                                                  value: tradeCategory,
                                                  label: TRADE_CATEGORIES.find(
                                                      (c) => c.value === tradeCategory
                                                  )?.label
                                              }
                                            : null
                                    }
                                    onChange={handleTradeCategoryChange}
                                    disabled={disabled}
                                    style={{ width: '100%', maxWidth: 300 }}
                                    allowClear
                                    labelInValue
                                >
                                    {TRADE_CATEGORIES.map((cat) => (
                                        <Option key={cat.value} value={cat.value} label={cat.label}>
                                            <Space>
                                                <span
                                                    style={{
                                                        display: 'inline-block',
                                                        width: 12,
                                                        height: 12,
                                                        borderRadius: '50%',
                                                        backgroundColor: cat.color
                                                    }}
                                                />
                                                <span>{cat.label}</span>
                                            </Space>
                                        </Option>
                                    ))}
                                </Select>
                                {tradeCategory && (
                                    <div style={{ marginTop: 8 }}>
                                        <Tag
                                            color={
                                                TRADE_CATEGORIES.find(
                                                    (c) => c.value === tradeCategory
                                                )?.color
                                            }
                                        >
                                            {
                                                TRADE_CATEGORIES.find(
                                                    (c) => c.value === tradeCategory
                                                )?.label
                                            }
                                        </Tag>
                                    </div>
                                )}
                            </div>

                            <Divider />

                            {/* Numeric Fields */}
                            <div className="evaluation-section">
                                <Title level={5}>Evaluation Metrics</Title>
                                <Text
                                    type="secondary"
                                    style={{ display: 'block', marginBottom: 12 }}
                                >
                                    Assign this trade to a set (max 20 trades per set). View sets in
                                    TradeBook Strategy Sets tab.
                                </Text>

                                {setValidationError && (
                                    <Alert
                                        message={setValidationError.message}
                                        type="warning"
                                        showIcon
                                        closable
                                        onClose={() => setSetValidationError(null)}
                                        style={{ marginBottom: 16 }}
                                    />
                                )}

                                <Row gutter={[16, 16]}>
                                    <Col xs={24} sm={8}>
                                        <div className="numeric-field">
                                            <Text>Strategy Set</Text>
                                            <InputNumber
                                                value={strategySet}
                                                onChange={(value) =>
                                                    handleNumericChange('strategySet', value)
                                                }
                                                disabled={disabled}
                                                style={{ width: '100%' }}
                                                placeholder="Enter set number"
                                                min={1}
                                                status={
                                                    setValidationError?.field === 'strategySet'
                                                        ? 'error'
                                                        : undefined
                                                }
                                            />
                                        </div>
                                    </Col>
                                    <Col xs={24} sm={8}>
                                        <div className="numeric-field">
                                            <Text>No Strategy Set</Text>
                                            <InputNumber
                                                value={noStrategySet}
                                                onChange={(value) =>
                                                    handleNumericChange('noStrategySet', value)
                                                }
                                                disabled={disabled}
                                                style={{ width: '100%' }}
                                                placeholder="Enter set number"
                                                min={1}
                                                status={
                                                    setValidationError?.field === 'noStrategySet'
                                                        ? 'error'
                                                        : undefined
                                                }
                                            />
                                        </div>
                                    </Col>
                                </Row>
                            </div>

                            <Divider />

                            {/* Actions */}
                            <div className="evaluation-actions">
                                <Space>
                                    <Button
                                        type="primary"
                                        icon={<SaveOutlined />}
                                        onClick={handleManualSave}
                                        loading={saveMutation.isLoading}
                                        disabled={disabled || !hasUnsavedChanges}
                                    >
                                        Save Evaluation
                                    </Button>
                                    <Popconfirm
                                        title="Clear evaluation?"
                                        description="This will remove all evaluation data from this trade."
                                        onConfirm={handleClear}
                                        okText="Yes, Clear"
                                        cancelText="Cancel"
                                    >
                                        <Button
                                            danger
                                            icon={<ClearOutlined />}
                                            loading={clearMutation.isLoading}
                                            disabled={disabled}
                                        >
                                            Clear Evaluation
                                        </Button>
                                    </Popconfirm>
                                </Space>
                                {hasUnsavedChanges && (
                                    <Text type="warning" className="unsaved-indicator">
                                        Unsaved changes
                                    </Text>
                                )}
                            </div>
                        </>
                    )}
                </>
            )}
        </div>
    );
}

TradeEvaluation.propTypes = {
    tradeId: PropTypes.string.isRequired,
    initialEvaluation: PropTypes.shape({
        tradingPlanRef: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
        // All checked criteria fields
        checkedTime: PropTypes.array,
        checkedIndicators: PropTypes.array,
        checkedConfirmations: PropTypes.array,
        checkedEntrySignal: PropTypes.array,
        checkedStopLoss: PropTypes.array,
        checkedTargets: PropTypes.array,
        checkedRiskPerTrade: PropTypes.array,
        checkedProfitTargetPerTrade: PropTypes.array,
        checkedMaxLossPerTrade: PropTypes.array,
        checkedStopLossMechanism: PropTypes.array,
        checkedPartialPlan: PropTypes.array,
        checkedAddingToPosition: PropTypes.array,
        checkedHowToManageStop: PropTypes.array,
        checkedSpecificRules: PropTypes.array,
        // Other fields
        strategySet: PropTypes.number,
        noStrategySet: PropTypes.number,
        tradeCategory: PropTypes.string
    }),
    disabled: PropTypes.bool
};

TradeEvaluation.defaultProps = {
    initialEvaluation: null,
    disabled: false
};
